/**
 * Tiny event emitter used across the game.
 * Core systems emit events (e.g. {type:'kill'}); UI/renderer subscribe for feedback.
 * No Phaser, no DOM — plain pub/sub so /core stays headless-testable.
 */

/** @typedef {(payload:any)=>void} Listener */

export class EventBus {
  constructor() {
    /** @type {Map<string, Set<Listener>>} */
    this._listeners = new Map();
  }

  /**
   * Subscribe to an event type.
   * @param {string} type
   * @param {Listener} fn
   * @returns {()=>void} unsubscribe function
   */
  on(type, fn) {
    if (!this._listeners.has(type)) this._listeners.set(type, new Set());
    this._listeners.get(type).add(fn);
    return () => this.off(type, fn);
  }

  /**
   * Unsubscribe a listener.
   * @param {string} type
   * @param {Listener} fn
   */
  off(type, fn) {
    this._listeners.get(type)?.delete(fn);
  }

  /**
   * Emit an event to all listeners of its type.
   * @param {string} type
   * @param {any} [payload]
   */
  emit(type, payload) {
    this._listeners.get(type)?.forEach((fn) => fn(payload));
  }
}

/** Shared bus instance for the running game. */
export const bus = new EventBus();
